import { useEffect, useMemo } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { signUpDataAtom, pushStepAtom, stepStackAtom } from "store/signUpStore";
import { useCheckDuplicateCtn } from "hooks/useCheckDuplicateCtn";
import { IdentityOtpForm, AuthType, IdentityOtpResult } from "components/identity/IdentityOtpForm";
import { getKrGenderDigit } from "utils/krGenderDigit";
import { toDanalCtn } from "utils/phoneNumber";

/**
 * Step5: 인증 단계 (OTP / 다날)
 *
 * stepStack 의 5 등장 횟수로 회차 판정:
 *   - 해외                       → OTP (SMS)
 *   - KR + 14세이상              → DANAL_SELF  (본인 다날 5항목)
 *   - KR + 14세미만 + 0회차       → DANAL_PARENT (보호자 다날 5항목)
 *   - KR + 14세미만 + 1회차       → OTP         (미성년자 휴대폰 SMS)
 *
 * 인증 성공 후 라우팅:
 *   - DANAL_PARENT          → step4 (미성년자 휴대폰 입력)
 *   - DANAL_SELF / OTP      → ctn 중복 체크 → step6/7
 */
export const SignUpStep5 = () => {
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);
    const stepStack = useAtomValue(stepStackAtom);
    const { checkCtn } = useCheckDuplicateCtn();

    const authType: AuthType = useMemo(() => {
        // 가입 연령 정책 적용 + 본인인증 미사용 → 국내·해외 모두 휴대폰 인증(SMS)
        if (signUpData.policyEnabled && !signUpData.idvEnabled) return "OTP";
        const isKorea = signUpData.countryCd === "KR";
        const isMinor = signUpData.parentConsent;
        // 현재 step(마지막) 제외, 이전 스택의 5 등장 횟수 → 0: 보호자 다날, 1: 미성년자 SMS
        const danalRound = stepStack.slice(0, -1).filter(s => s === 5).length;

        if (!isKorea) return "OTP";
        if (isMinor) return danalRound >= 1 ? "OTP" : "DANAL_PARENT";
        return "DANAL_SELF";  // KR 14세 이상 본인
    }, [signUpData.countryCd, signUpData.parentConsent, signUpData.policyEnabled, signUpData.idvEnabled, stepStack]);

    const isDanal = authType === "DANAL_PARENT" || authType === "DANAL_SELF";
    const isParentAuth = authType === "DANAL_PARENT";

    // 다날 5항목 계산 — OTP 면 undefined, 비정상 진입(stale 등)으로 데이터 불완전하면 null.
    //   getKrGenderDigit 는 birth/gender 가 형식에 안 맞으면 throw 하므로, render 중 throw 로
    //   흰 화면이 되지 않도록 호출 전에 사전 검증해 불완전하면 null 을 반환한다.
    const danalParams = useMemo(() => {
        if (!isDanal) return undefined;
        const birth = isParentAuth ? signUpData.parentBirth : signUpData.birth;
        const gender = isParentAuth ? signUpData.parentGender : signUpData.gender;
        const nationality = isParentAuth ? signUpData.parentNationality : signUpData.nationality;
        if (!/^\d{8}$/.test(birth) || (gender !== "MALE" && gender !== "FEMALE")) {
            return null;
        }
        return {
            name: isParentAuth ? signUpData.parentName : signUpData.name,
            // ctn 은 선행 0 없는 저장 형식이지만 다날은 국내 표기를 요구한다 → 여기서만 0 을 붙인다.
            phoneNumber: toDanalCtn((isParentAuth ? signUpData.parentCtn : signUpData.ctn) || ""),
            birth: birth.substring(2),  // YYYYMMDD → YYMMDD
            genderDigit: getKrGenderDigit(birth, gender, nationality === "FOREIGNER"),
            carrier: isParentAuth ? signUpData.parentTelecom : signUpData.telecom,
            parentConsent: isParentAuth,
        };
    }, [isDanal, isParentAuth, signUpData]);

    // 다날 인증 단계인데 5항목이 불완전 — 비정상 진입이므로 실패 화면으로
    useEffect(() => {
        if (danalParams === null) pushStep(8);
    }, [danalParams, pushStep]);

    const onSuccess = (result: IdentityOtpResult) => {
        if (result.authType === "DANAL_PARENT") {
            // 보호자 인증 완료 → 미성년자 휴대폰 입력(Step4)
            setSignUpData(prev => ({
                ...prev,
                parentIdentityVerificationId: result.identityVerificationId || "",
                // 법정대리인 동의 대상이면 다날로 인증된 보호자 이름·번호를 동의 요청 보호자 정보로 쓴다
                ...(prev.policyEnabled && prev.guardianRequired ? {
                    guardianName: prev.parentName,
                    guardianCtn: prev.parentCtn,
                    guardianCtnCountryCd: "+82",
                } : {}),
            }));
            pushStep(4);
            return;
        }

        if (result.authType === "DANAL_SELF") {
            setSignUpData(prev => ({
                ...prev,
                identityVerificationId: result.identityVerificationId || "",
            }));
        }
        if (result.authType === "OTP") {
            // 휴대폰 인증 결과 — 가입 요청에 전달해 서버가 가입 번호의 인증 여부를 확인한다
            setSignUpData(prev => ({ ...prev, verifyId: result.verifyId ?? null }));
        }

        // DANAL_SELF(14세 이상) 또는 OTP(미성년자 SMS / 해외) → ctn 중복 검사
        const ctn = signUpData.ctn;
        if (ctn) {
            checkCtn({
                serviceId: signUpData.serviceId,
                ctnCountryCd: signUpData.ctnCountryCd,
                ctn,
            });
        } else {
            pushStep(8);
        }
    };

    return (
        <IdentityOtpForm
            serviceId={signUpData.serviceId}
            authType={authType}
            danal={danalParams || undefined}
            sms={!isDanal ? {
                phoneNumber: signUpData.ctn || "",
                countryCode: signUpData.ctnCountryCd || "+82",
                verifyPurpose: "SIGNUP",
            } : undefined}
            onSuccess={onSuccess}
        />
    );
};
